import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../lib/responsiveImageSizes';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import { readBlockCtaIntent, resolveBlockCtaHref } from '../../lib/resolveSectionHref';
import type { BlockRenderProps } from '../../types';

/** Arrangement `Hero_Standard` — Block=Hero; Chrome N/A. */
export function HeroStandard({
  block,
  locale,
  defaultLocale = locale,
  isActive,
  activeEditTarget,
  editable,
  headingLevel = 'h1',
  pages,
  siteMode,
  allBlocks,
}: BlockRenderProps) {
  const Heading = headingLevel;
  const heading = pickBlockField(block.content, 'heading', locale);
  const subheading = pickBlockField(block.content, 'subheading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryIntent = readBlockCtaIntent(block.content, locale, {
    hrefFieldKey: 'ctaPrimaryHref',
    fallback: 'browse',
  });
  const ctaPrimaryHref = resolveBlockCtaHref(ctaPrimaryIntent, {
    locale,
    defaultLocale,
    content: block.content,
    hrefFieldKey: 'ctaPrimaryHref',
    siteMode,
    pages,
    allBlocks,
  });
  const sealImage = pickMediaUrl(block, 'heroImage');
  const sealSrc = sealImage ? buildSiteImageProxyUrl(sealImage, 240, 78) : undefined;
  const sealSrcSet = sealImage ? buildSiteImageSrcSet(sealImage, [120, 180, 240], 78) : undefined;
  const target = isActive ? activeEditTarget : null;

  return (
    <section
      className={['ws-hero-monolith', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="top"
    >
      <div id="about" className="ws-hero-monolith__inner">
        {sealSrc ? (
          <div className="ws-hero-monolith__seal">
            <EditableMedia
              fieldKey="heroImage"
              editable={editable}
              activeEditTarget={target}
              className="ws-hero-monolith__seal-img"
              src={sealSrc}
              srcSet={sealSrcSet}
              sizes={SITE_IMAGE_SIZES.heroMonolithSeal}
              alt={heading || 'Mark'}
              width={120}
              height={120}
              loading="eager"
              decoding="async"
            />
          </div>
        ) : null}
        <div className="ws-hero-monolith__rule" aria-hidden />
        <EditableContent
          as={Heading}
          fieldKey="heading"
          editable={editable}
          activeEditTarget={target}
          className="ws-hero-monolith__title"
        >
          {heading}
        </EditableContent>
        <EditableContent
          fieldKey="subheading"
          editable={editable}
          activeEditTarget={target}
          className="ws-hero-monolith__lead"
        >
          {subheading}
        </EditableContent>
        {ctaPrimary ? (
          <EditableContent
            as="a"
            fieldKey="ctaPrimary"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-monolith__link"
            href={ctaPrimaryHref}
          >
            {ctaPrimary}
          </EditableContent>
        ) : null}
      </div>
    </section>
  );
}
